import React, { useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@material-ui/core";
import { Add, Delete, Visibility } from "@material-ui/icons";
import { useFormContext, useWatch } from "react-hook-form";
import { css } from "@emotion/css";
import { LogoIcon } from "assets";
import { useSortableFieldArray } from "routes/createContent/views/lessons/hooks/useSortableFieldArray";
import { TemplateFormData, TemplateFormExercise } from "../types";
import { MAX_DURATION_WEEKS } from "../constants";
import ExerciseRow from "./ExerciseRow";
import ExercisePickerDialog from "./ExercisePickerDialog";
import ExercisePreviewDialog from "./ExercisePreviewDialog";

// Exercises are unordered on the template (the v2 payload has no per-exercise
// order/position), so this list is not drag-sortable — just add/remove. Rows
// share one column template with the header so every control lines up.
const ExercisesSection: React.VFC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const { control } = useFormContext<TemplateFormData>();
  const { fields, ids, append, remove } = useSortableFieldArray("exercises");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewId, setPreviewId] = useState<number | null>(null);

  const ongoing = useWatch({ control, name: "ongoing" }) as boolean;
  const durationWeeks = useWatch({ control, name: "duration_weeks" }) as number;
  const maxWeeks = ongoing ? MAX_DURATION_WEEKS : durationWeeks || 1;

  const existingIds = fields.map(f => f.exercise_id as number);

  const onAdd = (exercises: TemplateFormExercise[]) => exercises.forEach(ex => append(ex));

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="h6">Exercises</Typography>
        {!readOnly && (
          <Button startIcon={<Add />} size="small" style={{ marginLeft: 12 }} onClick={() => setPickerOpen(true)}>
            Add exercises
          </Button>
        )}
      </Box>

      {fields.length === 0 ? (
        <Typography color="textSecondary">No exercises added yet.</Typography>
      ) : (
        <>
          <div className={`${styles.grid} ${styles.gridHeader}`}>
            <span />
            <span>Exercise</span>
            <span>Start week</span>
            <span>End week</span>
            <span>Level</span>
            <span />
            <span>Sets</span>
            <span>Repetitions</span>
            <span />
          </div>

          {fields.map((f, i) => {
            const title = (f.title as string) || `Exercise ${i + 1}`;
            const imageUrl = f.image_url as string | null;
            return (
              <div key={ids[i]} className={styles.grid}>
                {imageUrl ? (
                  <img alt="" src={imageUrl} height={40} width={40} className={styles.thumb} />
                ) : (
                  <LogoIcon title="Joint Academy" style={{ height: 40, width: 40 }} />
                )}
                <Typography variant="subtitle2">{title}</Typography>
                <ExerciseRow
                  index={i}
                  maxWeeks={maxWeeks}
                  readOnly={readOnly}
                  defaults={f as unknown as TemplateFormExercise}
                />
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      aria-label={`Preview ${title}`}
                      onClick={() => setPreviewId(f.exercise_id as number)}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!readOnly && (
                    <Tooltip title="Remove">
                      <IconButton size="small" aria-label={`Remove ${title}`} onClick={() => remove(i)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </div>
            );
          })}
        </>
      )}

      <ExercisePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        existingIds={existingIds}
        onAdd={onAdd}
      />
      <ExercisePreviewDialog exerciseId={previewId} onClose={() => setPreviewId(null)} />
    </Box>
  );
};

const styles = {
  grid: css`
    display: grid;
    grid-template-columns: 40px minmax(140px, 1fr) 118px 118px 76px 34px 76px 130px 76px;
    gap: 10px;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #eee;
  `,
  gridHeader: css`
    border-bottom: 1px solid #ddd;
    padding: 2px 0 6px;
    & > span {
      font-size: 0.6875rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: rgba(0, 0, 0, 0.45);
    }
  `,
  thumb: css`
    object-fit: cover;
    border-radius: 4px;
  `,
};

export default ExercisesSection;
